import Swal from 'sweetalert2';
import i18n from '@/i18n';

// SweetAlert2 Toast configuration (Velzon style)
const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 5000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.addEventListener('mouseenter', Swal.stopTimer);
    toast.addEventListener('mouseleave', Swal.resumeTimer);
  },
  customClass: {
    popup: 'colored-toast',
  },
});

/**
 * L'ultimo errore mostrato, per non ripeterlo due volte di fila.
 *
 * Un salvataggio che fallisce fa scattare DUE toast identici: l'interceptor in api.js lo
 * mostra e poi rilancia, e il catch della schermata lo rimostra. Sono 71 i punti che fanno
 * cosi', e toglierlo da tutti vorrebbe dire lasciare mute quelle poche schermate che non
 * passano dall'interceptor. Si accorpa qui, che e' l'unico punto da cui passano tutti.
 *
 * Solo per gli errori, e solo per due secondi: due errori uguali a distanza di tempo sono
 * due fatti diversi e vanno detti tutte e due, ma lo stesso errore due volte nello stesso
 * istante e' sempre un'eco.
 */
let ultimoErrore = { chiave: null, quando: 0 };
const ECO_MS = 2000;

export const toastService = {
  success(message, title = null) {
    Toast.fire({
      icon: 'success',
      title: title || i18n.global.t('t-success'),
      text: message,
    });
  },

  error(message, title = null) {
    const titolo = title || i18n.global.t('t-error');
    const chiave = `${titolo}\u0000${message}`;
    const adesso = Date.now();
    if (ultimoErrore.chiave === chiave && adesso - ultimoErrore.quando < ECO_MS) {
      return;
    }
    ultimoErrore = { chiave, quando: adesso };
    Toast.fire({
      icon: 'error',
      title: titolo,
      text: message,
    });
  },

  warning(message, title = null) {
    Toast.fire({
      icon: 'warning',
      title: title || i18n.global.t('t-warning'),
      text: message,
    });
  },

  info(message, title = null) {
    Toast.fire({
      icon: 'info',
      title: title || i18n.global.t('t-info'),
      text: message,
    });
  },

  // Extract string message from various error formats
  extractMessage(value) {
    if (!value) return null;
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      // Try common error message field names
      if (typeof value.message === 'string') return value.message;
      if (typeof value.error === 'string') return value.error;
      if (typeof value.detail === 'string') return value.detail;
      if (typeof value.errorMessage === 'string') return value.errorMessage;
      // Array of errors (e.g., validation errors)
      if (Array.isArray(value) && value.length > 0) {
        return value.map(v => this.extractMessage(v)).filter(Boolean).join(', ');
      }
      // Try to stringify if nothing else works, but avoid [object Object]
      try {
        const str = JSON.stringify(value);
        if (str && str !== '{}' && str !== '[]') {
          return str.length > 200 ? str.substring(0, 200) + '...' : str;
        }
      } catch (e) {
        // ignore
      }
    }
    return null;
  },

  /**
   * Il messaggio d'errore del server nella lingua scelta, se il suo codice ne ha una (t-err-CODICE).
   *
   * I valori che il messaggio cita arrivano a parte (error.params) e si rimettono dentro. Quelli
   * che sono a loro volta codici — per esempio le parti di indirizzo mancanti, COORDINATE o CITTA —
   * si traducono uno per uno (t-err-val-CODICE); il resto, come il nome di una camera, resta com'e'.
   *
   * Quando lo stesso codice nasce da campi diversi che vogliono parole diverse, il testo del campo
   * (t-err-CODICE-campo, col campo di error.params) vale prima di quello comune: GESTIONE_NOTA_OBBLIGATORIA
   * e' il perche' delle commissioni scritte a mano (campo motivo) o quello della rettifica (rettificaNota).
   *
   * Torna null quando non c'e' traduzione: allora vale il testo del server, come sempre.
   */
  tradotto(errore) {
    const codice = errore?.code;
    if (!codice || typeof codice !== 'string') return null;
    const esiste = (k) => i18n.global.te(k) || i18n.global.te(k, 'it');
    const campo = errore.params?.campo;
    const delCampo = typeof campo === 'string' ? `t-err-${codice}-${campo}` : null;
    const chiave = delCampo && esiste(delCampo) ? delCampo : `t-err-${codice}`;
    if (!esiste(chiave)) return null;
    const unValore = (x) => {
      if (typeof x !== 'string') return x;
      if (i18n.global.te(`t-err-val-${x}`)) return i18n.global.t(`t-err-val-${x}`);
      // Un campo di una riga per canale (canali.EXPEDIA.trattenuta, canali.BOOKING.percentuale): coi punti
      // vue-i18n cerca un percorso fra le chiavi, e una chiave scritta cosi' non la trova. L'etichetta si
      // compone: la voce (t-err-val-canali-<voce>) col nome del canale, come lo scrivono le schermate.
      const perCanale = /^canali\.([A-Z]+)\.([A-Za-z]+)$/.exec(x);
      if (perCanale && i18n.global.te(`t-err-val-canali-${perCanale[2]}`)) {
        const nome = [`t-addebiti-canale-${perCanale[1]}`, perCanale[1] === 'DIRETTA' ? 't-gestione-canali-dirette' : null]
          .find((k) => k && i18n.global.te(k));
        return i18n.global.t(`t-err-val-canali-${perCanale[2]}`, { canale: nome ? i18n.global.t(nome) : perCanale[1] });
      }
      // Un campo dell'aspetto dei documenti (Fase 5, V6: aspetto.colore, aspetto.piedeNote...): coi punti, come i canali,
      // e la sua chiave col trattino (t-err-val-aspetto-colore).
      const dellAspetto = /^aspetto\.([A-Za-z]+)$/.exec(x);
      if (dellAspetto && i18n.global.te(`t-err-val-aspetto-${dellAspetto[1]}`)) return i18n.global.t(`t-err-val-aspetto-${dellAspetto[1]}`);
      return x;
    };
    const valori = {};
    Object.entries(errore.params || {}).forEach(([nome, valore]) => {
      valori[nome] = Array.isArray(valore) ? valore.map(unValore).join(', ') : unValore(valore);
    });
    // L'indice di una riga mandato dal server conta da zero (GESTIONE_VOCE_NON_VALIDA: la prima voce e' 0); nel testo e'
    // la riga che si vede, da uno ("riga 1" per la prima, come il messaggio italiano del server).
    if (Number.isInteger(errore.params?.indice)) valori.indice = errore.params.indice + 1;
    return i18n.global.t(chiave, valori);
  },

  // Parse API error and show toast
  apiError(error) {
    let message = i18n.global.t('t-generic-error');
    let title = i18n.global.t('t-error');

    if (error.response) {
      const { status, data } = error.response;

      // Try to get message from API response (handle both string and object)
      // Backend uses ApiResponse with ErrorDetails: { success, data, error: { code, message } }
      const extractedMessage = this.extractMessage(data?.error?.message)  // ApiResponse.ErrorDetails.message
        || this.extractMessage(data?.error)  // fallback if error is a string
        || this.extractMessage(data?.message)
        || this.extractMessage(data?.detail)
        || this.extractMessage(data?.errorMessage)
        || (typeof data === 'string' ? data : null);

      // Il messaggio del server e' in italiano. Se il suo codice ha una traduzione si mostra quella,
      // cosi' chi usa l'inglese non si trova "Il codice e' scaduto" in mezzo a una schermata inglese.
      const tradotto = this.tradotto(data?.error);
      if (tradotto) {
        message = tradotto;
      } else if (extractedMessage) {
        message = extractedMessage;
      }

      // Set title based on status code
      switch (status) {
        case 400:
          title = i18n.global.t('t-bad-request');
          break;
        case 401:
          title = i18n.global.t('t-unauthorized');
          break;
        case 403:
          title = i18n.global.t('t-forbidden');
          break;
        case 404:
          title = i18n.global.t('t-not-found');
          break;
        case 500:
          title = i18n.global.t('t-server-error');
          break;
        case 502:
        case 503:
        case 504:
          title = i18n.global.t('t-service-unavailable');
          if (!extractedMessage) {
            message = i18n.global.t('t-service-unavailable-message');
          }
          break;
        default:
          title = i18n.global.t('t-error');
      }
    } else if (error.request) {
      // Network error
      title = i18n.global.t('t-connection-error');
      message = i18n.global.t('t-connection-error-message');
    } else {
      // Other error
      message = error.message || i18n.global.t('t-generic-error');
    }

    this.error(message, title);
  },
};

export default toastService;
